/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { Table } from '@/wpt-lib/pdf/common/Table';
import { useActivitiesFocusArea, useLabels } from '@/wpt-lib/pdf/useData';
import Heading from '@/wpt-lib/pdf/common/Heading';
import { View } from '@react-pdf/renderer';
import { ActivityFocusArea } from '@/wpt-lib/interfaces/Activity';
import { groupBy, sortBy } from 'lodash';
import NameKPI from '@/wpt-lib/pdf/activity/NameKPI';
import Leadership from '@/wpt-lib/pdf/activity/Leadership';
import Strategy2027 from '@/wpt-lib/pdf/activity/Strategy2027';
import ProgressUpdates from '@/wpt-lib/pdf/ProgressUpdates';
import Collaborators from '@/wpt-lib/pdf/activity/Collaborators';

import ServiceEngagement from '@/wpt-lib/pdf/activity/service/ServiceEngagement';
import Other from '@/wpt-lib/pdf/activity/service/Other';
import ProfessionalDevelopment from '@/wpt-lib/pdf/activity/service/ProfessionalDevelopment';
import ServiceBenchmark from '@/wpt-lib/pdf/activity/service/ServiceBenchmark';
import Comments from '@/wpt-lib/pdf/Comments';
import OutsideWork from '@/wpt-lib/pdf/activity/service/OutsideWork';
import Engagement from '@/wpt-lib/pdf/activity/service/Engagement';
import InOrAboveLoad from '@/wpt-lib/pdf/activity/teaching/InOrAboveLoad';

const ActivityTitles: {
    [type: string]: string;
} = {
    engagement: 'Engagement',
    'external-engagement-and-service': 'Internal Engagement',
    'internal-engagement-and-service': 'External Engagement',
    'non-base-service-engagement': 'Non-Base Engagement',
    'outside-work': 'Outside Work',
    other: 'Other Service & Engagement Activity',
    'professional-development': 'Professional Development Service & Engagement Activity',
    'service-engagement': 'Service',
};

const ActivityComponents: {
    // eslint-disable-next-line
    [type: string]: React.ComponentType<any>;
} = {
    engagement: Engagement,
    'external-engagement-and-service': ServiceEngagement,
    'internal-engagement-and-service': ServiceEngagement,
    'non-base-service-engagement': Other,
    other: Other,
    'outside-work': OutsideWork,
    'professional-development': ProfessionalDevelopment,
    'service-engagement': ServiceEngagement,
};

const ServiceActivities = () => {
    const order = Object.keys(ActivityTitles);
    const service = sortBy(
        Object.entries(
            groupBy(useActivitiesFocusArea(ActivityFocusArea.ENGAGEMENT), (a) => `${a.data.serviceEngagement}`),
        ),
        (e) => order.indexOf(e[0]),
    );

    const titleLabels = useLabels('serviceEngagement');

    return service.length > 0 ? (
        <Page>
            <Heading text="Service & Engagement Activities" />

            {service.map(([type, typeActivities]) => (
                <View key={type}>
                    <Heading text={titleLabels[type] ?? ActivityTitles[type]} level={2} />

                    {typeActivities.map((activity, i) => (
                        <Table cols={4} key={i}>
                            <NameKPI {...activity.data} />
                            {type in ActivityComponents &&
                                React.createElement(ActivityComponents[type], {
                                    ...activity.data,
                                })}
                            <Collaborators {...activity.data} />
                            <Leadership {...activity.data} />
                            <InOrAboveLoad {...activity.data} />
                            <ServiceBenchmark {...activity.data} />
                            <Strategy2027 {...activity.data} />
                            <ProgressUpdates item={activity.workplanItem} />
                            <Comments comments={activity.workplanItem.comments} />
                        </Table>
                    ))}
                </View>
            ))}
        </Page>
    ) : null;
};

export default ServiceActivities;
